import { ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import coin from "../assets/images/placeholder/crypto-colour.png";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import AssetSelectionContent from "./AssetSelectionContent";

interface Asset {
  assetId: string;
  asset: string;
  assetIcon?: string;
  assetName?: string;
  availableAmount?: number;
  availableAmountFormatted?: string;
  valueInLocalCurrency?: string;
  [key: string]: unknown;
}

interface AssetSelectionDropdownProps {
  selectedAsset: Asset | null | undefined;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  filteredAssets: Asset[] | undefined;
  onAssetSelect: (asset: Asset) => void;
  disabled?: boolean;
  showBalance?: boolean;
  formatter?: {
    format: (amount: number | undefined, currency: string) => string;
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isLoading?: boolean;
  matchTriggerWidth?: boolean;
}

export default function AssetSelectionDropdown({
  selectedAsset,
  searchTerm,
  onSearchChange,
  filteredAssets,
  onAssetSelect,
  disabled = false,
  showBalance = false,
  formatter,
  open,
  onOpenChange,
  isLoading = false,
  matchTriggerWidth = true,
}: AssetSelectionDropdownProps) {
  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange} modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          disabled={disabled}
          variant="outline"
          className="w-full flex items-center justify-between py-8 rounded-2xl hover:border-brand-primary transition-colors h-[70px] md:h-[88px]"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 md:w-10 h-8 md:h-10 rounded-full flex items-center justify-center">
              {selectedAsset ? (
                <img
                  src={selectedAsset.assetIcon}
                  alt={selectedAsset.assetName ?? ""}
                  width={32}
                  height={32}
                  className="text-white max-w-full h-auto"
                />
              ) : (
                <img
                  src={coin}
                  alt={"Default Asset"}
                  width={32}
                  height={32}
                  className="text-white max-w-full h-auto"
                />
              )}
            </div>
            <div className="text-left">
              <div className="font-semibold">
                {selectedAsset?.asset.toUpperCase()}
              </div>
              <div className="font-semibold text-sm md:text-base">
                {selectedAsset?.assetName ?? "Select..."}
              </div>
            </div>
          </div>
          <div className="p-3">
            <ChevronDown className="text-gray-400 w-4 h-4 md:w-5 md:h-5" />
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="rounded-2xl p-6"
        align="start"
        style={
          matchTriggerWidth
            ? { width: "var(--radix-dropdown-menu-trigger-width)" }
            : undefined
        }
      >
        <AssetSelectionContent
          searchTerm={searchTerm}
          onSearchChange={onSearchChange}
          filteredAssets={filteredAssets}
          onAssetSelect={onAssetSelect}
          showBalance={showBalance}
          formatter={formatter}
          isLoading={isLoading}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
